import React, { Component } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../static/css/EventDetail.css';
import logo from '../static/images/images/a-logo-transperent.png';
import AppStoreButtons from './AppStoreButtons';
import PublicEvents from './PublicEvents';
import { resolveEventArtwork } from './vibeArt';
import { readLocal } from '../safeStorage';
import { trackScreen, SCREEN } from '../analytics';

// Inline SVG icons, module-level consts, same as eyeIcon/eyeOffIcon/cameraIcon elsewhere in this app.
//
// 🔒 THESE MUST NOT GO BACK TO EMOJI. `color: #ED9594` does nothing to 📍 — an emoji carries its own
// palette from the system font, so it cannot be tinted and it renders differently on macOS, Windows
// and Android. These are stroke outlines on `currentColor`, so one CSS `color` drives all of them.
// Shapes follow Material Symbols (location_on / calendar_month / schedule / groups), Apache-2.0.
// All four share a 24x24 viewBox and stroke-width 1.5 so the meta row is optically even.
const locationIcon = (
    <svg className="event-detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21.3c4.2-4 6.4-7.3 6.4-10.4a6.4 6.4 0 1 0-12.8 0c0 3.1 2.2 6.4 6.4 10.4Z" />
        <circle cx="12" cy="10.7" r="2.4" />
    </svg>
);

const calendarIcon = (
    <svg className="event-detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3.3" y="5" width="17.4" height="15.7" rx="2.6" />
        <path d="M3.3 9.9h17.4" />
        <path d="M8.2 3.3v3.4" />
        <path d="M15.8 3.3v3.4" />
    </svg>
);

const clockIcon = (
    <svg className="event-detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.6" />
        <path d="M12 6.9v5.4l3.5 2.1" />
    </svg>
);

const groupsIcon = (
    <svg className="event-detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9.2" cy="9.1" r="3.1" />
        <path d="M3.3 19.4a5.9 5.9 0 0 1 11.8 0" />
        <circle cx="17.2" cy="10.6" r="2.3" />
        <path d="M17.2 15.1a5 5 0 0 1 3.5 4.3" />
    </svg>
);

// Same shape as groupsIcon, sized for the Who's Going card. Duplicated rather than parameterised —
// this app copy-pastes rather than abstracting, and the two are styled by different classes.
const groupsIconLarge = (
    <svg className="event-detail-going-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="9.2" cy="9.1" r="3.1" />
        <path d="M3.3 19.4a5.9 5.9 0 0 1 11.8 0" />
        <circle cx="17.2" cy="10.6" r="2.3" />
        <path d="M17.2 15.1a5 5 0 0 1 3.5 4.3" />
    </svg>
);

// SEO constants. www is canonical — the bare apex 302-redirects to it, so every URL emitted here
// must carry the www or the structured data points crawlers at a redirect.
const SITE_ORIGIN = 'https://www.animates.app';
const JSON_LD_ID = 'event-detail-jsonld';

const shareIcon = (
    <svg className="event-detail-share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="17.5" cy="5.8" r="2.8" />
        <circle cx="6.5" cy="12" r="2.8" />
        <circle cx="17.5" cy="18.2" r="2.8" />
        <path d="M9 10.6 15 7.2" />
        <path d="M9 13.4 15 16.8" />
    </svg>
);

// The public event share page, /e/{slug}. Unauthenticated: GET /public/events/:slug takes no
// Authorization header, and the page renders the same for a signed-in user and an anonymous one.
//
// ⚠️ The projection carries city and state only — never a street address, never coordinates, never
// any attendee identity. That is the server's decision and this page must not try to enrich it.
//
// Link previews are SERVER-rendered. In production a-server answers /e/{slug} itself
// (a-server/routes/eventPagePreview.js): it fetches public/index.html as the shell and injects the
// per-event title, description and image, so crawlers that do not run JavaScript still get a real
// preview. Nothing in this component affects previews — document.title below is for the browser tab
// and Googlebot's rendered pass. Locally, /e/ view-source shows the generic tags; that is expected.
// Do not reach for react-helmet.
//
// 🔒 Because the server uses public/index.html as that shell, `<div id="root">` and `</head>` there
// must stay byte-identical — its looksLikeShell() check requires both.
class EventDetailInner extends Component {
    constructor() {
        super();
        this.state = { status: 'loading', event: null, shareMessage: '' };
        this.previousTitle = null;
        this.previousDescription = null;
        this.previousCanonical = null;
        this.shareTimer = null;
        this.onBack = this.onBack.bind(this);
        this.onShare = this.onShare.bind(this);
    }

    componentDidMount() {
        // Captured here, NOT in the constructor. The wrapper's key={slug} remounts this component on a
        // card-to-card navigation, and React constructs the new instance BEFORE the old one's
        // componentWillUnmount restores the title — so the constructor would capture the previous
        // EVENT's title and leave it behind when the visitor leaves the page.
        this.previousTitle = document.title;

        // screen_view, deduped module-scope in analytics.js — do NOT add a local guard.
        // No-ops entirely when signed out.
        trackScreen(SCREEN.EVENT_DETAILS);

        const slug = this.props.params.slug;
        axios.get(process.env.REACT_APP_SERVER_API + '/public/events/' + encodeURIComponent(slug))
            .then((response) => {
                // Detail returns { item: {...} } — note the singular, and note it is NOT the
                // { items, paging } envelope the browse endpoint uses.
                const event = response.data && response.data.item;
                if (!event) {
                    this.setState({ status: 'notfound' });
                    return;
                }
                this.applySeo(event);
                this.setState({ status: 'ready', event: event });
            })
            .catch((err) => {
                // An unknown or malformed slug is a 404. Anything else is a real failure, but the
                // visitor sees the same honest page either way — a blank screen or a silent redirect
                // would hide the problem.
                const status = err.response && err.response.status;
                if (status !== 404) {
                    console.error('Could not load event ' + slug + ':', err);
                }
                this.setState({ status: status === 404 ? 'notfound' : 'failed' });
            });
    }

    componentWillUnmount() {
        this.clearSeo();
        if (this.shareTimer) clearTimeout(this.shareTimer);
    }

    // ---- SEO ---------------------------------------------------------------------------------
    // Googlebot runs JavaScript on a second crawl pass, so a runtime <title>, <meta name="description">
    // and JSON-LD block are all indexed. ⚠️ Social crawlers (Discord, iMessage, Facebook, Slack) do
    // NOT run JS, so none of this improves link previews — that needs server-rendered HTML and is a
    // separate, already-documented decision. Do not reach for react-helmet to "fix" previews.
    // Everything set here is undone in clearSeo() so a client-side navigation cannot leave a stale
    // event's title, description or structured data behind.

    // "{event} · {City}, ST · AniMates", kept under 60 chars or Google truncates it. Degrades by
    // dropping the location first, then trimming the event name — never by shipping something long.
    buildTitle = (event) => {
        const location = [event.event_city, event.event_state].filter(Boolean).join(', ');
        const full = [event.event_title, location, 'AniMates'].filter(Boolean).join(' · ');
        if (full.length <= 60) return full;
        const short = [event.event_title, 'AniMates'].filter(Boolean).join(' · ');
        if (short.length <= 60) return short;
        return String(event.event_title).slice(0, 48).trim() + '… · AniMates';
    };

    // One sentence: name, city, date. ~155 chars max, which is roughly where Google cuts a snippet.
    // ⚠️ No attendee count — it is 0 on most curated events, so it must read correctly on an event
    // nobody has joined.
    buildDescription = (event) => {
        const location = [event.event_city, event.event_state].filter(Boolean).join(', ');
        const sentence = event.event_title
            + (location ? ' in ' + location : '')
            + (event.event_date ? ' on ' + event.event_date : '')
            + '. See the details and get the AniMates app to join.';
        return sentence.length <= 155 ? sentence : sentence.slice(0, 154).trim() + '…';
    };

    // "6:00 PM" -> "18:00". Returns null on anything unexpected, so a value we cannot parse omits
    // endDate rather than emitting a broken one.
    // ⚠️ event_timezone ("America/Los_Angeles") is deliberately NOT used. Local time with no offset
    // is valid schema.org and Google accepts it; guessing an offset risks a wrong startDate, which is
    // worse than no offset at all.
    to24Hour = (value) => {
        const match = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i.exec(String(value || ''));
        if (!match) return null;
        const hour = (Number(match[1]) % 12) + (/PM/i.test(match[3]) ? 12 : 0);
        return String(hour).padStart(2, '0') + ':' + match[2];
    };

    // Has this event ended? Mirrors the server's UPCOMING_FILTER (a-server/routes/events.js), in the
    // event's own timezone: ended if its date is before today there, or it is today, it has an end
    // time, and end + 1 hour has passed. One deliberate difference: an end time earlier than the start
    // means it crosses midnight, and the payload has no end DATE, so that never counts as ended today.
    // 🔒 Never `new Date('YYYY-MM-DD')` — that is UTC midnight (see dateParts in PublicEvents.js). The
    // dates are compared as YYYY-MM-DD strings and the times as minutes.
    isEnded = (event) => {
        const date = String(event.event_date_iso || '');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;

        let today = null;
        let nowTime = null;
        try {
            // A null zone, or an unknown one (Intl throws a RangeError), falls back to the
            // visitor's clock below.
            if (!event.event_timezone) throw new Error('no event timezone');
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: event.event_timezone,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
            }).formatToParts(new Date());
            const get = (type) => (parts.find((part) => part.type === type) || {}).value;
            today = get('year') + '-' + get('month') + '-' + get('day');
            nowTime = get('hour') + ':' + get('minute');
        } catch (err) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            today = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
            nowTime = pad(now.getHours()) + ':' + pad(now.getMinutes());
        }

        if (date < today) return true;
        if (date > today) return false;

        const end = this.to24Hour(event.event_end_time);
        const start = event.event_time_24h;
        if (!end || !start || !(end > start)) return false;
        const minutes = (hhmm) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
        return minutes(end) + 60 < minutes(nowTime);
    };

    // schema.org/Event — this is what makes the page eligible for Google's event rich results.
    // 🔒 Every key is omitted when its value is missing. Malformed JSON-LD is worse than none:
    // Google drops the entire block rather than the bad key.
    buildEventJsonLd = (event) => {
        const data = {
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: event.event_title,
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        };

        if (event.event_date_iso && event.event_time_24h) {
            data.startDate = event.event_date_iso + 'T' + event.event_time_24h;
            const end = this.to24Hour(event.event_end_time);
            // Only when it is genuinely later than the start. The payload carries no end DATE, so an
            // earlier end time means the event crosses midnight and we cannot express it correctly —
            // omit rather than emit an endDate before the startDate.
            if (end && end > event.event_time_24h) {
                data.endDate = event.event_date_iso + 'T' + end;
            }
        }

        // 🔒 CITY, STATE AND COUNTRY ONLY — never a street address and never coordinates. The
        // projection does not carry them and this must never start emitting them; it is the same
        // privacy line the whole public-events feature is built on.
        const placeName = [event.event_city, event.event_state].filter(Boolean).join(', ');
        if (placeName) {
            const address = { '@type': 'PostalAddress' };
            if (event.event_city) address.addressLocality = event.event_city;
            if (event.event_state) address.addressRegion = event.event_state;
            if (event.event_country_code) address.addressCountry = event.event_country_code;
            data.location = { '@type': 'Place', name: placeName, address: address };
        }

        if (event.event_image) data.image = [event.event_image];
        if (event.event_description) data.description = event.event_description;
        if (event.public_slug) data.url = SITE_ORIGIN + '/e/' + event.public_slug;
        data.organizer = { '@type': 'Organization', name: 'AniMates', url: SITE_ORIGIN + '/' };
        return data;
    };

    applySeo(event) {
        document.title = this.buildTitle(event);

        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            this.previousDescription = meta.getAttribute('content');
            meta.setAttribute('content', this.buildDescription(event));
        }

        // 🔴 public/index.html ships a STATIC canonical pointing at the homepage, so without this
        // every /e/{slug} told Google "my canonical is https://www.animates.app/" and all of them
        // were consolidated into the homepage — "Duplicate, Google chose different canonical" on
        // every event URL, and a sitemap of URLs that each disown themselves.
        // ⚠️ Unlike the og:/twitter: tags this DOES work at runtime: Googlebot renders the page with
        // headless Chromium and reads the canonical from the rendered DOM, the same reason the
        // runtime title and JSON-LD are indexed. Social crawlers still never see it — that is the
        // separate, documented server-rendering decision and the og: comment above stays true.
        // Kept identical to buildEventJsonLd's `url` (both SITE_ORIGIN + '/e/' + slug): a canonical
        // that disagrees with the JSON-LD url is its own conflicting signal.
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical && event.public_slug) {
            this.previousCanonical = canonical.getAttribute('href');
            canonical.setAttribute('href', SITE_ORIGIN + '/e/' + event.public_slug);
        }

        const existing = document.getElementById(JSON_LD_ID);
        if (existing) existing.remove();
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = JSON_LD_ID;
        script.text = JSON.stringify(this.buildEventJsonLd(event));
        document.head.appendChild(script);
    }

    clearSeo() {
        if (this.previousTitle !== null) document.title = this.previousTitle;
        const meta = document.querySelector('meta[name="description"]');
        if (meta && this.previousDescription !== null) {
            meta.setAttribute('content', this.previousDescription);
        }
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical && this.previousCanonical !== null) {
            canonical.setAttribute('href', this.previousCanonical);
        }
        const script = document.getElementById(JSON_LD_ID);
        if (script) script.remove();
    }

    // Called from render() (:320, :386) and from fallbackPath below, so this MUST NOT throw:
    // a blocked store here took the whole public /e/:slug page down. readLocal returns null instead.
    signedIn = () => !!readLocal('token');

    // Where "back" goes when there is nowhere to go back to.
    fallbackPath = () => (this.signedIn() ? '/home' : '/');

    // 🔒 The usual visitor to this page followed a link pasted into a Discord or a text message and
    // has NO in-app history — a bare navigate(-1) there either does nothing or takes them off the
    // site entirely. React Router gives the very first entry in a session the literal key 'default',
    // so that is the test for "did they actually navigate here from inside the app".
    onBack() {
        if (this.props.location && this.props.location.key !== 'default') {
            this.props.navigate(-1);
            return;
        }
        this.props.navigate(this.fallbackPath());
    }

    flashShareMessage(message) {
        if (this.shareTimer) clearTimeout(this.shareTimer);
        this.setState({ shareMessage: message });
        this.shareTimer = setTimeout(() => this.setState({ shareMessage: '' }), 2500);
    }

    // ⚠️ navigator.share and navigator.clipboard both require a secure context, so BOTH are absent on
    // plain http://localhost and on any http:// host. Every branch is feature-detected and wrapped,
    // so the button degrades through share -> clipboard -> a readable instruction instead of throwing.
    async onShare() {
        const url = window.location.href;
        const title = (this.state.event && this.state.event.event_title) || 'AniMates';
        if (navigator.share) {
            try {
                await navigator.share({ title: title, url: url });
                return;
            } catch (err) {
                // Dismissing the share sheet rejects with AbortError. That is the user saying no —
                // silently copying the link instead would be the wrong answer.
                if (err && err.name === 'AbortError') return;
            }
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                this.flashShareMessage('Link copied');
                return;
            } catch (err) {
                // Fall through to the instruction below.
            }
        }
        this.flashShareMessage('Copy this page’s URL to share it');
    }

    renderHeader() {
        // Same pattern as the splash header (Welcome.js): a Home link when signed in, Sign In +
        // Sign Up when not. Deliberately NOT a "Sign Out" link and deliberately no hamburger —
        // neither exists anywhere else in this app.
        return (
            <div className="event-detail-header">
                <Link to="/"><img className="event-detail-header-logo" src={logo} alt="AniMates" /></Link>
                <div className="event-detail-header-auth-links">
                    {this.signedIn() ? (
                        <Link to="/home" className="event-detail-header-auth-link">Home</Link>
                    ) : (
                        <>
                            <Link to="/signin" className="event-detail-header-auth-link">Sign In</Link>
                            <Link to="/signup" className="event-detail-header-auth-link">Sign Up</Link>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Same treatment as the splash footer (Welcome.js). Rendered as a SIBLING of
    // .event-detail-content, so on desktop it spans the full page beneath both grid columns rather
    // than sitting inside the content column.
    // The Privacy link is included deliberately: this is a page strangers land on cold from a link
    // pasted in a chat, and the legal pages should be reachable from it. The splash's "AniMates is
    // operated by GX Studios LLC." line is deliberately NOT carried over — the owner asked only for
    // the copyright line, and the operator notice belongs on the marketing page.
    // ⚠️ The year is computed. Welcome.js:124 hardcodes 2026; that is pre-existing and out of scope.
    renderFooter() {
        return (
            <p className="event-detail-footer">
                <span className="event-detail-footer-item">&copy; {new Date().getFullYear()} AniMates. All rights reserved.</span>
                <Link to="/privacy" className="event-detail-footer-link no-dec">Privacy</Link>
            </p>
        );
    }

    // The store CTA. Renders at zero attendees too — it is the entire point of the page — and its
    // copy never references a count, because most curated events sit at zero.
    renderGoingCard(ended) {
        return (
            <aside className="event-detail-going-card">
                {/* Section LABEL, not a headline — top-left, sized like "Vibe / Activity" and
                    "Details" so the card reads as a labelled section rather than a floating panel
                    beside two labelled ones. The old "See who's going on the app!" heading is gone on
                    purpose: with the label restored it was the third restatement of the same idea in
                    one card, alongside this label and the body's "...to see who's attending...".
                    "Power Level" is the app's real feature name — it has its own /how-to-power-level
                    page and is spelled exactly this way in all 9 uses there. Do not paraphrase it to
                    "status" or "rank". No counts anywhere in this copy either: attendee_count is 0 on
                    most curated events, so it has to read correctly on an event nobody has joined. */}
                <h2 className="event-detail-going-label" style={{fontFamily: "'Chelsea Market', cursive"}}>Who&rsquo;s Going</h2>
                {groupsIconLarge}
                {/* An ended event has nobody left to see attending, so point at the next one. */}
                <p className="event-detail-going-copy">
                    {ended
                        ? 'Download AniMates to find the next meetup near you.'
                        : <>Download AniMates to see who&rsquo;s attending, connect with other fans, level up your Power Level, and host meetups of your own.</>}
                </p>
                <AppStoreButtons variant="eventdetail" />
            </aside>
        );
    }

    // Not-found is also what a CANCELLED event looks like (cancelled events are deleted, as are the
    // events of deleted accounts). Ended events no longer land here — the server returns them — so
    // the page offers the upcoming list and the store badges instead of a dead end.
    renderNotFound(failed) {
        return (
            <div className="event-detail-page">
                {this.renderHeader()}
                <div className="event-detail-content">
                <div className="event-detail-notfound">
                    <h1 className="event-detail-notfound-title" style={{fontFamily: "'Chelsea Market', cursive"}}>
                        {failed ? 'Something went wrong' : 'Event not found'}
                    </h1>
                    <p className="event-detail-notfound-copy">
                        {failed
                            ? 'We could not load this event just now. Please try again in a moment.'
                            : 'This event may have been cancelled, or the link may be incorrect.'}
                    </p>
                    <Link to={this.fallbackPath()} className="event-detail-back">
                        {this.signedIn() ? 'Go to your account' : 'Go to AniMates'}
                    </Link>
                    <AppStoreButtons variant="eventdetail" />
                </div>
                {/* Hides itself when there are no upcoming events or the fetch fails. */}
                <PublicEvents />
                </div>
                {this.renderFooter()}
            </div>
        );
    }

    // Skeleton for the fixed-height parts above the description, so the banner, title and meta row
    // do not move when the data lands. The Who's Going card is static copy, so it renders for real.
    renderSkeleton() {
        return (
            <div className="event-detail-page">
                {this.renderHeader()}
                <div className="event-detail-content">
                    <button type="button" className="event-detail-back" onClick={this.onBack}>&larr; Back</button>
                    <div className="event-detail-layout">
                        <div className="event-detail-main">
                            <div className="event-detail-banner event-detail-banner-skeleton"></div>
                            <span className="event-detail-skeleton-bar event-detail-skeleton-title"></span>
                            <span className="event-detail-skeleton-bar event-detail-skeleton-meta"></span>
                        </div>
                        {this.renderGoingCard()}
                    </div>
                </div>
                {this.renderFooter()}
            </div>
        );
    }

    render() {
        const { status, event, shareMessage } = this.state;

        if (status === 'notfound' || status === 'failed') return this.renderNotFound(status === 'failed');
        if (status === 'loading') return this.renderSkeleton();

        const location = [event.event_city, event.event_state].filter(Boolean).join(', ');
        const hasAttendees = typeof event.attendee_count === 'number' && event.attendee_count > 0;
        const ended = this.isEnded(event);

        // Built as a list and joined with separators, so an absent item cannot leave a dangling `·`
        // or an empty column. attendee_count is 0 on most curated events, so that is the common case,
        // not an edge case. event_date already carries the weekday ("Tue, September 15, 2026").
        const metaItems = [];
        if (location) metaItems.push({ key: 'location', icon: locationIcon, text: location });
        if (event.event_date) metaItems.push({ key: 'date', icon: calendarIcon, text: event.event_date });
        if (event.event_time) metaItems.push({ key: 'time', icon: clockIcon, text: event.event_time });
        if (hasAttendees) metaItems.push({ key: 'going', icon: groupsIcon, text: event.attendee_count + ' Going' });

        return (
            <div className="event-detail-page">
                {this.renderHeader()}
                <div className="event-detail-content">
                    <button type="button" className="event-detail-back" onClick={this.onBack}>&larr; Back</button>
                    <div className="event-detail-layout">
                        <div className="event-detail-main">
                            <div className="event-detail-banner">
                                {/* Always renders now. resolveEventArtwork falls back to the vibe
                                    artwork when there is no event_image — user-created meetups can
                                    never have one, since uploads are staff-gated. The CSS gradient
                                    behind it is the last resort if even that 404s. */}
                                <img
                                    className="event-detail-banner-image"
                                    src={resolveEventArtwork(event)}
                                    alt=""
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <div className="event-detail-banner-scrim"></div>
                                {event.vibe_tag && (
                                    <span className="event-detail-vibe-pill">{event.vibe_tag}</span>
                                )}
                                <button
                                    type="button"
                                    className="event-detail-share"
                                    onClick={this.onShare}
                                    aria-label="Share this event"
                                >
                                    {shareIcon} Share
                                </button>
                                {shareMessage && (
                                    <span className="event-detail-share-message" role="status">{shareMessage}</span>
                                )}
                            </div>

                            <h1 className="event-detail-title" style={{fontFamily: "'Chelsea Market', cursive"}}>{event.event_title}</h1>

                            {ended && (
                                <p className="event-detail-ended">This event has ended</p>
                            )}

                            <p className="event-detail-meta">
                                {metaItems.map((meta, index) => (
                                    <React.Fragment key={meta.key}>
                                        {index > 0 && <span className="event-detail-meta-dot">·</span>}
                                        <span className="event-detail-meta-item">
                                            {meta.icon} {meta.text}
                                        </span>
                                    </React.Fragment>
                                ))}
                            </p>

                            {event.vibe_tag && (
                                <section className="event-detail-section">
                                    <h2 className="event-detail-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Vibe / Activity</h2>
                                    <span className="event-detail-vibe-chip">{event.vibe_tag}</span>
                                </section>
                            )}

                            {event.event_description && (
                                <section className="event-detail-section">
                                    <h2 className="event-detail-section-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Details</h2>
                                    <p className="event-detail-description">{event.event_description}</p>
                                </section>
                            )}
                        </div>

                        {this.renderGoingCard(ended)}
                    </div>
                    {/* A sibling BELOW the layout, not inside it, so on desktop it spans both grid
                        columns. The Who's Going card above keeps the page's only store-badge row. */}
                    {ended && <PublicEvents />}
                </div>
                {this.renderFooter()}
            </div>
        );
    }
}

// Wrapper to pass React Router v6 params to class component
function EventDetail() {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // 🔒 key={slug}: a PublicEvents card on this page links to another /e/{slug}, and React Router
    // keeps this route's element mounted across that navigation. EventDetailInner fetches only in
    // componentDidMount, so without the key the page stayed on the old event. The key remounts it.
    return <EventDetailInner key={params.slug} params={params} location={location} navigate={navigate} />;
}

export default EventDetail;
